import React, { useCallback, useMemo, useState } from "react";
import { compile, morph } from "svg-path-morph";
import { MoveToCommand, parseSVG } from "svg-path-parser";
import { Glyph } from "./Glyph";
import { RadialKnob } from "./RadialKnob";

interface Anchor {
  x: number;
  y: number;
}

export interface SvgFileData {
  absPath: string;
  path: string;
  fileName: string;
  signature: string;
  compiled?: ReturnType<typeof compile>;
  viewBox?: string;
}

interface GroupMorphProps {
  sig: string;
  files: SvgFileData[];
  baselineSignature?: string;
}

function getStartingPoint(pathStr: string) {
  return parseSVG(pathStr)[0];
}

function isDifferent(
  referenceStartingPoint: MoveToCommand,
  currentStartingPoint: MoveToCommand
) {
  if (!referenceStartingPoint || !currentStartingPoint) return false;

  const tolerance = 5; // Adjust tolerance as needed (pixels)
  const dx = referenceStartingPoint.x - currentStartingPoint.x;
  const dy = referenceStartingPoint.y - currentStartingPoint.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  return distance > tolerance;
}

function highlightDifferences(refSig: string, sig: string): JSX.Element {
  const highlighted: JSX.Element[] = [];
  const maxLength = Math.max(refSig.length, sig.length);

  for (let i = 0; i < maxLength; i++) {
    const refChar = refSig[i] || "";
    const char = sig[i] || "";
    const style = char !== refChar ? { backgroundColor: "#ffc9c9" } : undefined;

    highlighted.push(
      <span key={i} style={style}>
        {char || " "}
      </span>
    );
  }

  return <>{highlighted}</>;
}

function computeAnchors(count: number, radius: number): Anchor[] {
  const anchors: Anchor[] = [];
  const angleStep = (2 * Math.PI) / count;
  for (let i = 0; i < count; i++) {
    const angle = i * angleStep;
    anchors.push({ x: radius * Math.cos(angle), y: radius * Math.sin(angle) });
  }
  return anchors;
}

export const GroupMorph: React.FC<GroupMorphProps> = ({
  sig,
  files,
  baselineSignature,
}) => {
  const anchors = computeAnchors(files.length, 78);
  const [morphedPath, setMorphedPath] = useState<string>("");

  const compiledForGroup = compile(files.map((fileData) => fileData.absPath));

  const handleWeightsChangeForGroup = useCallback(
    (sig: string, weights: number[]) => {
      try {
        if (!compiledForGroup) return;

        setMorphedPath(morph(compiledForGroup, weights));
      } catch (e) {
        console.error(e);
      }
    },
    [compiledForGroup]
  );

  const referenceStartingPoint = useMemo(() => {
    return getStartingPoint(files[0].absPath) as MoveToCommand;
  }, [files]);

  return (
    <div
      style={{
        padding: "1rem",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        backgroundColor: "#f9f9f9",
      }}
    >
      <h3 style={{ margin: "0 0 1rem 0" }}>
        {baselineSignature ? highlightDifferences(baselineSignature, sig) : sig}
      </h3>
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        {files.map((fileData, idx) => {
          const currentStartingPoint = getStartingPoint(
            fileData.absPath
          ) as MoveToCommand;

          const isDifferentStartingPoint = isDifferent(
            referenceStartingPoint,
            currentStartingPoint
          );

          return (
            <>
              <Glyph
                caption={fileData.fileName}
                key={`${idx}`}
                path={fileData.path}
                isSingleGlyph={files.length === 1}
                color={isDifferentStartingPoint ? "#FFCCCB" : undefined}
                viewBox={fileData.viewBox}
              />
            </>
          );
        })}
        {files.length >= 2 && (
          <>
            <Glyph
              caption="Interpolated Path"
              key="morphed"
              path={morphedPath}
              color="lightblue"
              viewBox={files[0].viewBox}
            />
            <RadialKnob
              radius={78}
              anchors={anchors}
              onWeightsChange={(weights) =>
                handleWeightsChangeForGroup(sig, weights)
              }
            />
          </>
        )}
      </div>
    </div>
  );
};
